import {
  createStyles,
  Fab,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'
import React, { useState } from 'react'
import {
  BooleanField,
  BooleanInput,
  Datagrid,
  DeleteButton,
  FunctionField,
  List,
  ListProps,
  NumberField,
  NumberInput,
  Pagination,
  required,
  sanitizeListRestProps,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useListContext,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Link } from 'react-router-dom'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import {
  CreateDialog,
  CreateDialogProps,
} from '../../common/customized-ra-components/CreateDialog'
import {
  EditDialog,
  EditDialogProps,
} from '../../common/customized-ra-components/EditDialog'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { Authority } from '../../../core/auth/Authority'
import { TextFieldWithMaxChars } from '../../common/TextFieldWithMaxChars'
import { PrintAnchor } from '../../../core/enum/PrintAnchor'
import { printQRCodeItemDto } from '../../../core/dto/print-qrcode-item.dto'
import { QRCodeCorrectionLevels } from '../../../core/enum/QRCodeCorrectionLevels'
import { RAColorPicker } from '../../common/RAColorPicker'
import { RAColorField } from '../../common/RAColorField'
import {
  ItemParametersGridExpand,
  printParameterTypeChoices,
} from './ItemParametersGridExpand'

const useStyles = makeStyles((theme: any) =>
  createStyles({
    editToolbar: {
      display: 'flex',
      '&>button:last-child': {
        marginLeft: 'auto',
      },
    },
    doubleInputDiv: {
      display: 'flex',
      gap: '16px',
      '& > *': {
        minWidth: '210px',
      },
    },
    tripleInputDiv: {
      display: 'flex',
      gap: '16px',
      '& > *': {
        minWidth: '134px',
      },
    },
    floating: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main,
      margin: 0,
      top: 'auto',
      right: 20,
      bottom: 60,
      left: 'auto',
      position: 'fixed',
      zIndex: 1000,
    },
    smallScreenDialogCorrection: {
      marginTop: '220px',
    },
  }),
)

const validatePrintQRCodeItems = (values: printQRCodeItemDto) => {
  const errors: { [n: string]: string } = {}
  const requiredFields = [
    'xPosition',
    'yPosition',
    'anchor',
    'code',
    'priority',
    'qrCodeCorrectionLevel',
    'moduleSize',
    'darkHexColor',
    'lightHexColor',
  ] as const
  requiredFields.forEach((field) => {
    if (!values[field as keyof typeof values]) {
      errors[field] = 'ra.validation.required'
    }
  })
  return errors
}

const EditToolbar = ({ ...props }) => {
  const { handleClose, invalid, ...rest } = props
  const classes = useStyles()
  const hasAuthority = useHasAuthority()

  return (
    <Toolbar {...rest} className={classes.editToolbar}>
      <SaveButton disabled={invalid} redirect={false} onClick={handleClose} />
      <DeleteButton
        redirect={false}
        onClick={handleClose}
        disabled={!hasAuthority(Authority.DELETE_PRINT_QRCODE_ITEMS)}
      />
    </Toolbar>
  )
}

const QRCodeItemEditDialog = (props: EditDialogProps) => {
  const { handleClose, id: itemId } = props
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <EditDialog {...props} title={`ID ${itemId}`}>
      <SimpleForm
        redirect={false}
        toolbar={<EditToolbar handleClose={handleClose} />}
        validate={validatePrintQRCodeItems}
      >
        <TextInput
          source="code"
          validate={required()}
          fullWidth
          className={
            smallScreen ? classes.smallScreenDialogCorrection : undefined
          }
        />
        <div className={!smallScreen ? classes.tripleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="xPosition"
            validate={required()}
            fullWidth
          />
          <NumberInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="yPosition"
            validate={required()}
            fullWidth
          />
          <SelectInput
            source="anchor"
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintAnchor.TOP_LEFT,
                PrintAnchor.TOP_CENTER,
                PrintAnchor.TOP_RIGHT,
                PrintAnchor.CENTER_LEFT,
                PrintAnchor.CENTER,
                PrintAnchor.CENTER_RIGHT,
                PrintAnchor.BOTTOM_LEFT,
                PrintAnchor.BOTTOM_CENTER,
                PrintAnchor.BOTTOM_RIGHT,
              ].map((anchor) => ({
                id: anchor,
                name: `resources.enums.printAnchors.${anchor}`,
              })),
            ]}
          />
        </div>
        <div className={!smallScreen ? classes.tripleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="moduleSize"
            validate={required()}
            fullWidth
          />
          <SelectInput
            source="qrCodeCorrectionLevel"
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                QRCodeCorrectionLevels.L,
                QRCodeCorrectionLevels.M,
                QRCodeCorrectionLevels.Q,
                QRCodeCorrectionLevels.H,
              ].map((level) => ({
                id: level,
                name: `resources.enums.printQRCodeCorrectionLevels.${level}`,
              })),
            ]}
          />
          <BooleanInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="addQuietZone"
            fullWidth
          />
        </div>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <RAColorPicker
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="darkHexColor"
            fullWidth
          />
          <RAColorPicker
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="lightHexColor"
            fullWidth
          />
        </div>
        <NumberInput source="priority" validate={required()} fullWidth />
      </SimpleForm>
    </EditDialog>
  )
}

const CreateToolbar = ({ ...props }) => {
  const { handleClose, invalid, ...rest } = props

  return (
    <Toolbar {...rest}>
      <SaveButton disabled={invalid} redirect={false} onClick={handleClose} />
    </Toolbar>
  )
}

const QRCodeItemCreateDialog = (props: CreateDialogProps) => {
  const { handleClose } = props
  const { record: printLayout } = useShowContext()
  const translate = useTranslate()
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <CreateDialog {...props}>
      <SimpleForm
        initialValues={{
          printLayoutId: printLayout?.id,
          anchor: PrintAnchor.TOP_LEFT,
          darkHexColor: '#000000',
          lightHexColor: '#FFFFFF',
          priority: 1,
        }}
        redirect={false}
        toolbar={<CreateToolbar handleClose={handleClose} />}
        validate={validatePrintQRCodeItems}
      >
        <TextInput
          source="code"
          validate={required()}
          fullWidth
          className={
            smallScreen ? classes.smallScreenDialogCorrection : undefined
          }
        />
        <div className={!smallScreen ? classes.tripleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="xPosition"
            validate={required()}
            fullWidth
          />
          <NumberInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="yPosition"
            validate={required()}
            fullWidth
          />
          <SelectInput
            source="anchor"
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                PrintAnchor.TOP_LEFT,
                PrintAnchor.TOP_CENTER,
                PrintAnchor.TOP_RIGHT,
                PrintAnchor.CENTER_LEFT,
                PrintAnchor.CENTER,
                PrintAnchor.CENTER_RIGHT,
                PrintAnchor.BOTTOM_LEFT,
                PrintAnchor.BOTTOM_CENTER,
                PrintAnchor.BOTTOM_RIGHT,
              ].map((anchor) => ({
                id: anchor,
                name: `resources.enums.printAnchors.${anchor}`,
              })),
            ]}
          />
        </div>
        <div className={!smallScreen ? classes.tripleInputDiv : undefined}>
          <NumberInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="moduleSize"
            validate={required()}
            fullWidth
          />
          <SelectInput
            source="qrCodeCorrectionLevel"
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            fullWidth
            choices={[
              ...[
                QRCodeCorrectionLevels.L,
                QRCodeCorrectionLevels.M,
                QRCodeCorrectionLevels.Q,
                QRCodeCorrectionLevels.H,
              ].map((level) => ({
                id: level,
                name: `resources.enums.printQRCodeCorrectionLevels.${level}`,
              })),
            ]}
          />
          <BooleanInput
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="addQuietZone"
            fullWidth
          />
        </div>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <RAColorPicker
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="darkHexColor"
            fullWidth
          />
          <RAColorPicker
            resource={ResourceName.PRINT_QRCODE_ITEMS}
            source="lightHexColor"
            fullWidth
          />
        </div>
        <NumberInput source="priority" validate={required()} fullWidth />
        <Typography>
          {translate('resources.print-item-parameters.titles.create-inline')}
        </Typography>
        <div className={!smallScreen ? classes.doubleInputDiv : undefined}>
          <TextInput
            label="resources.print-item-parameters.fields.key"
            source="newParamKey"
          />
          <TextInput
            label="resources.print-item-parameters.fields.options"
            source="newParamOptions"
          />
        </div>
        <SelectInput
          label="resources.print-item-parameters.fields.type"
          source="newParamType"
          choices={printParameterTypeChoices}
          fullWidth
        />
      </SimpleForm>
    </CreateDialog>
  )
}

const PrintQRCodeItemsActions = ({ ...props }) => {
  const { handleOpenCreate, ...rest } = props
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <TopToolbar {...sanitizeListRestProps(rest)}>
      <Button
        label={translate('ra.action.create')}
        onClick={handleOpenCreate}
        startIcon={<Add />}
        disabled={
          !hasAuthority(Authority.EDIT_PRINT_LAYOUTS) ||
          !hasAuthority(Authority.CREATE_PRINT_QRCODE_ITEMS)
        }
      />
      {smallScreen && (
        <Fab
          component={Link}
          to="#"
          className={classes.floating}
          onClick={handleOpenCreate}
        >
          <Add />
        </Fab>
      )}
    </TopToolbar>
  )
}

const PrintQRCodeItemsGrid = ({ ...props }) => {
  const { handleOpenEdit, ...rest } = props
  const translate = useTranslate()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()

  return (
    <Datagrid
      {...rest}
      isRowExpandable={() => !smallScreen}
      expand={<ItemParametersGridExpand />}
      padding={smallScreen ? 'checkbox' : 'default'}
      hasBulkActions={false}
      rowClick={
        hasAuthority(Authority.EDIT_PRINT_QRCODE_ITEMS) &&
        hasAuthority(Authority.EDIT_PRINT_ITEM_PARAMETERS)
          ? handleOpenEdit
          : undefined
      }
      rowStyle={
        hasAuthority(Authority.EDIT_PRINT_QRCODE_ITEMS)
          ? () => ({
              color: 'default',
              cursor: 'pointer',
            })
          : undefined
      }
    >
      <TextField source="id" label="ID" />
      <TextFieldWithMaxChars source="code" maxChars={smallScreen ? 20 : 40} />
      {!smallScreen && <NumberField source="xPosition" />}
      {!smallScreen && <NumberField source="yPosition" />}
      {!smallScreen && (
        <FunctionField
          source="anchor"
          render={(record) =>
            translate(`resources.enums.printAnchors.${record?.anchor}`)
          }
        />
      )}
      {!smallScreen && (
        <RAColorField source="darkHexColor" emptyValue="#000000" ignoreLabel />
      )}
      {!smallScreen && (
        <RAColorField source="lightHexColor" emptyValue="#000000" ignoreLabel />
      )}
      {!smallScreen && (
        <FunctionField
          source="qrCodeCorrectionLevel"
          render={(record) =>
            translate(
              `resources.enums.printQRCodeCorrectionLevels.${record?.qrCodeCorrectionLevel}`,
            )
          }
        />
      )}
      {!smallScreen && <BooleanField source="addQuietZone" />}
      {!smallScreen && <NumberField source="priority" />}
    </Datagrid>
  )
}

export const PrintQRCodeItemsList = (props: ListProps) => {
  const { basePath } = useListContext()
  const [createOpen, setCreateOpen] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [editId, setEditId] = useState<string | undefined>(undefined)
  const hasAuthority = useHasAuthority()

  const handleOpenCreate = () => {
    setCreateOpen(true)
  }

  const handleCloseCreate = () => {
    setCreateOpen(false)
  }

  const handleOpenEdit = (id: string) => {
    if (hasAuthority(Authority.EDIT_PRINT_QRCODE_ITEMS)) {
      setEditId(id)
      setEditOpen(true)
    }
  }

  const handleCloseEdit = () => {
    setEditOpen(false)
  }

  return (
    <>
      <QRCodeItemCreateDialog
        {...props}
        basePath={`/${ResourceName.PRINT_QRCODE_ITEMS}`}
        handleClose={handleCloseCreate}
        resource={ResourceName.PRINT_QRCODE_ITEMS}
        open={createOpen}
      />
      <QRCodeItemEditDialog
        {...props}
        basePath={`/${ResourceName.PRINT_QRCODE_ITEMS}`}
        handleClose={handleCloseEdit}
        id={editId as string}
        resource={ResourceName.PRINT_QRCODE_ITEMS}
        open={editOpen}
      />
      <List
        {...props}
        actions={
          <PrintQRCodeItemsActions handleOpenCreate={handleOpenCreate} />
        }
        basePath={basePath}
        bulkActionButtons={false}
        empty={false}
        exporter={false}
        pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
        perPage={20}
      >
        <PrintQRCodeItemsGrid handleOpenEdit={handleOpenEdit} />
      </List>
    </>
  )
}
